import { type FC } from "react";
import { useTypedSelector } from "../../redux/store";
import { useGetAllGlobalMessages } from "../../hooks/useGetAllGlobalMessages";
import { useMutation } from "react-query";
import { ChatService } from "../../api";
import { Chat } from "../../components/Chat";
import type { UserResult } from "../../api/generated/data-contracts";
import { createSendingMessage } from "./utils";
import { useDispatch } from "react-redux";
import { addGlobalMessage } from "../../redux/slices/globalChat";
import { toast } from "react-toastify";

export const GlobalChat: FC = () => {
  const messages = useTypedSelector((state) => state.globalChat.messages);
  const currentUser = useTypedSelector((state) => state.auth.currentUser);
  const userJwt = useTypedSelector((state) => state.auth.value);
  const dispatch = useDispatch();
  const { isLoading } = useGetAllGlobalMessages({
    offset: undefined,
    limit: undefined
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      content,
      uuid
    }: {
      content: string;
      uuid: string;
    }) => {
      return ChatService.globalMessageCreate(
        { request: content, uuid },
        {
          headers: {
            Authorization: `Bearer ${userJwt}`
          }
        }
      );
    },
    onError: () => {
      toast.error("The message was not sent");
    }
  });

  const sender: UserResult = {
    userName: currentUser?.userName ?? "",
    firstName: currentUser?.fullName ?? "",
    lastName: currentUser?.fullName ?? "",
    fullName: currentUser?.fullName ?? currentUser?.userName ?? "",
    auth0Id: currentUser?.auth0Id ?? ""
  };

  const handleSendMessage = (message: string): void => {
    const msg = createSendingMessage(message, sender);

    dispatch(addGlobalMessage(msg));

    sendMessageMutation.mutate({ content: message, uuid: msg.uuid });
  };

  const deleteMessageMutation = useMutation({
    mutationFn: async (id: number) => {
      return ChatService.globalMessagesDelete(id, {
        headers: {
          Authorization: `Bearer ${userJwt}`
        }
      });
    },
    onError: () => {
      toast.error("Message was not deleted!");
    }
  });

  const handleDelete = (messageId: number): void => {
    deleteMessageMutation.mutate(messageId);
  };

  return (
    <Chat
      messages={messages}
      isLoading={isLoading}
      handleSendMessage={handleSendMessage}
      chatTitle="Global Chat"
      handleDelete={handleDelete}
      isGlobal
    />
  );
};
